import React, { useState } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Lock, Mail } from "lucide-react";
import { API } from "@/lib/api";
import { APP_NAME } from "@/lib/constants";
import { saveAdminToken } from "@/lib/admin-session";

const LoginScreen = ({ onAuthenticated, onBack, sandboxInvite = null }) => {
  const [mode, setMode] = useState(() => {
    if (sandboxInvite) return "register";
    const p = new URLSearchParams(window.location.search);
    return (p.get("register") === "1" || p.get("mode") === "register") ? "register" : "login";
  }); // 'login' | 'register'
  const [email, setEmail] = useState(() => sandboxInvite?.email || "");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      if (mode === "register") {
        const res = await axios.post(`${API}/auth/register`, {
          email,
          password,
          company_name: companyName,
          ...(sandboxInvite ? { personal_demo_token: sandboxInvite.token } : {}),
        });
        saveAdminToken(res.data.token);
        // Ein vollständiger Neustart der App vermeidet einen inkonsistenten
        // React-Zustand nach einem frischen Login und lädt die Organisation
        // immer mit der gerade gespeicherten Sitzung.
        window.location.replace("/app");
        return;
      } else {
        const res = await axios.post(`${API}/auth/login`, { email, password });
        saveAdminToken(res.data.token);
        // Siehe Registrierung: nach erfolgreicher Authentifizierung die App
        // gezielt frisch starten, statt Daten im alten Login-Baum zu laden.
        window.location.replace("/app");
        return;
      }
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Vorgang fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };



  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--brand-bg)] font-[Inter] px-4">
      <Card className="p-8 bg-white border-[var(--brand-border)] w-full max-w-md shadow-xl">
        <div className="flex items-center gap-3 mb-6">
          <img src="/logo-studio-suite.png" alt="Business Studio Suite" className="h-14 w-14 rounded-xl object-contain" />
          <div>
            <div className="font-[Playfair_Display] text-xl font-bold tracking-wide text-[var(--brand-ink)]">
              {APP_NAME}
            </div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-[var(--brand-primary)]">
              {sandboxInvite && mode === "register" ? "Persönliche Sandbox" : mode === "register" ? "Konto erstellen" : "Anmelden"}
            </div>
          </div>
        </div>

        {sandboxInvite && mode === "register" && (
          <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950" data-testid="sandbox-registration-intro">
            <strong>Willkommen in deiner persönlichen Sandbox.</strong>
            <p className="mt-1 leading-5">
              Erstelle jetzt dein eigenes Passwort. Alle sechs Studios sind für deinen zeitlich begrenzten Test freigeschaltet. Du kannst die Sandbox jederzeit selbst löschen.
            </p>
          </div>
        )}

        <div className="flex gap-2 mb-5 p-1 rounded-lg bg-[var(--brand-bg)]">
          <button
            type="button"
            onClick={() => { setMode("login"); setErr(""); }}
            className={`flex-1 py-2 text-sm rounded-md transition ${mode === "login" ? "bg-white shadow font-semibold text-[var(--brand-ink)]" : "text-[var(--brand-muted)]"}`}
            data-testid="tab-login"
          >
            Anmelden
          </button>
          <button
            type="button"
            onClick={() => { setMode("register"); setErr(""); }}
            className={`flex-1 py-2 text-sm rounded-md transition ${mode === "register" ? "bg-white shadow font-semibold text-[var(--brand-ink)]" : "text-[var(--brand-muted)]"}`}
            data-testid="tab-register"
          >
            Registrieren
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <div>
              <Label>Firmenname</Label>
              <Input
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="z. B. Muster GmbH"
                data-testid="register-company"
              />
            </div>
          )}
          <div>
            <Label>E-Mail</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              readOnly={Boolean(sandboxInvite && mode === "register")}
              autoFocus
              data-testid="login-email"
              required
            />
          </div>
          <div>
            <Label>Passwort</Label>
            <div className="relative">
              <Input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                placeholder={mode === "register" ? "min. 8 Zeichen" : ""}
                className="pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--brand-muted)] hover:text-[var(--brand-ink)]"
                aria-label={showPw ? "Passwort verbergen" : "Passwort anzeigen"}
                data-testid="toggle-password"
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          {mode === "login" && (
            <div className="text-right -mt-2">
              <a
                href="/reset-password"
                className="text-xs text-[var(--brand-muted)] hover:text-[var(--brand-ink)] hover:underline"
                onClick={(e) => { e.preventDefault(); window.location.href = "/reset-password"; }}
              >
                Passwort vergessen?
              </a>
            </div>
          )}
          {err && (
            <div
              className="text-sm rounded p-2"
              style={{ background: "#FEF2F2", color: "#991B1B" }}
              data-testid="login-error"
            >
              {err}
            </div>
          )}
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white"
            data-testid="login-submit"
          >
            <Lock className="w-4 h-4 mr-1" />
            {loading
              ? "Bitte warten…"
              : mode === "register"
              ? "Konto erstellen"
              : "Anmelden"}
          </Button>

          <div className="relative my-1">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-[var(--brand-border)]" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-3 text-xs text-[var(--brand-muted)]">oder</span>
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            disabled={loading}
            onClick={googleLogin}
            className="w-full border-[var(--brand-border)] text-[var(--brand-ink)] hover:bg-[var(--brand-border-light)]"
            data-testid="google-login-btn"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
            </svg>
            Mit Google anmelden
          </Button>

          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="w-full text-xs text-[var(--brand-muted)] hover:text-[var(--brand-ink)] hover:underline pt-1"
              data-testid="login-back-link"
            >
              ← Zurück zur Startseite
            </button>
          )}
        </form>
      </Card>
    </div>
  );
};

export { LoginScreen };
